import { createSlice } from '@reduxjs/toolkit';
import * as api from '../api';

/**
 * Redux Slice для курсов V1 (упрощенная версия)
 * 
 * Работает с реальным API бэкенда через ../api.js
 * Структура: Курсы → Потоки → Тарифы
 */

const initialState = {
    isLoading: false,
    error: null,
    
    // Курсы
    courses: [],
    currentCourse: null,
    
    // Потоки (flows)
    flows: [],
    currentFlow: null,
    
    // Тарифы
    tariffs: [],
    currentTariff: null,
};

const slice = createSlice({
    name: 'course-v1',
    initialState,
    reducers: {
        // ========== Общие состояния ==========
        startLoading(state) {
            state.isLoading = true;
            state.error = null;
        },
        stopLoading(state) {
            state.isLoading = false;
        },
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },

        // ========== Курсы ==========
        setCoursesSuccess(state, action) {
            state.isLoading = false;
            // Убедимся, что courses всегда массив
            state.courses = Array.isArray(action.payload) ? action.payload : [];
            state.error = null;
        },
        setCurrentCourseSuccess(state, action) {
            state.isLoading = false;
            state.currentCourse = action.payload;
            state.error = null;
        },
        clearCurrentCourse(state) {
            state.currentCourse = null;
            state.flows = [];
            state.currentFlow = null;
            state.tariffs = [];
        },
        addCourseSuccess(state, action) {
            state.isLoading = false;
            state.courses.push(action.payload);
            state.error = null;
        },
        updateCourseSuccess(state, action) {
            state.isLoading = false;
            const index = state.courses.findIndex((c) => c.id === action.payload.id);
            if (index !== -1) {
                state.courses[index] = action.payload;
            }
            if (state.currentCourse?.id === action.payload.id) {
                state.currentCourse = action.payload;
            }
            state.error = null;
        },
        deleteCourseSuccess(state, action) {
            state.isLoading = false;
            state.courses = state.courses.filter((c) => c.id !== action.payload);
            if (state.currentCourse?.id === action.payload) {
                state.currentCourse = null;
                state.flows = [];
            }
            state.error = null;
        },

        // ========== Потоки ==========
        setFlowsSuccess(state, action) {
            state.isLoading = false;
            // Убедимся, что flows всегда массив
            state.flows = Array.isArray(action.payload) ? action.payload : [];
            state.error = null;
        },
        setCurrentFlowSuccess(state, action) {
            state.isLoading = false;
            state.currentFlow = action.payload;
            state.error = null;
        },
        clearCurrentFlow(state) {
            state.currentFlow = null;
            state.tariffs = [];
        },
        addFlowSuccess(state, action) {
            console.log('[Redux] addFlowSuccess - flows ДО:', state.flows.length, state.flows);
            console.log('[Redux] addFlowSuccess - добавляем:', action.payload);
            state.isLoading = false;
            state.flows.push(action.payload);
            console.log('[Redux] addFlowSuccess - flows ПОСЛЕ:', state.flows.length, state.flows);
            state.error = null;
        },
        updateFlowSuccess(state, action) {
            state.isLoading = false;
            const index = state.flows.findIndex((f) => f.id === action.payload.id);
            if (index !== -1) {
                state.flows[index] = action.payload;
            }
            if (state.currentFlow?.id === action.payload.id) {
                state.currentFlow = action.payload;
            }
            state.error = null;
        },
        deleteFlowSuccess(state, action) {
            state.isLoading = false;
            state.flows = state.flows.filter((f) => f.id !== action.payload);
            if (state.currentFlow?.id === action.payload) {
                state.currentFlow = null;
                state.tariffs = [];
            }
            state.error = null;
        },

        // ========== Тарифы ==========
        setTariffsSuccess(state, action) {
            state.isLoading = false;
            // Убедимся, что tariffs всегда массив
            state.tariffs = Array.isArray(action.payload) ? action.payload : [];
            state.error = null;
        },
        setCurrentTariffSuccess(state, action) {
            state.isLoading = false;
            state.currentTariff = action.payload;
            state.error = null;
        },
        clearCurrentTariff(state) {
            state.currentTariff = null;
        },
        addTariffSuccess(state, action) {
            state.isLoading = false;
            state.tariffs.push(action.payload);
            state.error = null;
        },
        updateTariffSuccess(state, action) {
            state.isLoading = false;
            const index = state.tariffs.findIndex((t) => t.id === action.payload.id);
            if (index !== -1) {
                state.tariffs[index] = action.payload;
            }
            if (state.currentTariff?.id === action.payload.id) {
                state.currentTariff = action.payload;
            }
            state.error = null;
        },
        deleteTariffSuccess(state, action) {
            state.isLoading = false;
            state.tariffs = state.tariffs.filter((t) => t.id !== action.payload);
            if (state.currentTariff?.id === action.payload) {
                state.currentTariff = null;
            }
            state.error = null;
        },
    },
});

export default slice.reducer;
export const { actions } = slice;

// ============================================
// THUNKS - КУРСЫ
// ============================================

export function getCourses(shopId) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const data = await api.fetchCourses(shopId);
            dispatch(slice.actions.setCoursesSuccess(data));
        } catch (error) {
            console.error('Error fetching courses:', error);
            dispatch(slice.actions.hasError(error.message));
        }
    };
}

export function getCourse(shopId, courseId) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            console.log('[Redux] getCourse - загружаем курс:', { shopId, courseId });
            const data = await api.fetchCourse(shopId, courseId);
            console.log('[Redux] getCourse - получены данные курса:', data);

            // Load curriculum (modules + lessons) via dedicated endpoints.
            // Many existing UI pieces expect currentCourse.modules to be present.
            let modules = [];
            try {
                const rawModules = await api.fetchModules(shopId, courseId);
                const moduleList = Array.isArray(rawModules) ? rawModules : [];

                const modulesWithLessons = await Promise.all(
                    moduleList.map(async (m) => {
                        const moduleId = m?.id ?? m?.module_id;
                        if (!moduleId) return { ...m, lessons: [] };
                        try {
                            const lessons = await api.fetchLessons(shopId, courseId, moduleId);
                            return { ...m, id: moduleId, lessons: Array.isArray(lessons) ? lessons : [] };
                        } catch (e) {
                            console.warn('Failed to load lessons for module', moduleId, e);
                            return { ...m, id: moduleId, lessons: [] };
                        }
                    })
                );

                modules = modulesWithLessons;
            } catch (e) {
                console.warn('Failed to load modules for course', courseId, e);
            }

            const merged = { ...(data || {}), modules };
            dispatch(slice.actions.setCurrentCourseSuccess(merged));
            return merged;
        } catch (error) {
            console.error('Error fetching course:', error);
            dispatch(slice.actions.hasError(error.message));
            throw error;
        }
    };
}

export function createNewCourse(shopId, courseData) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const data = await api.createCourse(shopId, courseData);
            dispatch(slice.actions.addCourseSuccess(data));
            return data;
        } catch (error) {
            console.error('Error creating course:', error);
            dispatch(slice.actions.hasError(error.message));
            throw error;
        }
    };
}

export function updateExistingCourse(shopId, courseId, courseData) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const data = await api.updateCourse(shopId, courseId, courseData);
            dispatch(slice.actions.updateCourseSuccess(data));
            return data;
        } catch (error) {
            console.error('Error updating course:', error);
            dispatch(slice.actions.hasError(error.message));
            throw error;
        }
    };
}

export function removeCourse(shopId, courseId) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            await api.deleteCourse(shopId, courseId);
            dispatch(slice.actions.deleteCourseSuccess(courseId));
        } catch (error) {
            console.error('Error deleting course:', error);
            dispatch(slice.actions.hasError(error.message));
            throw error;
        }
    };
}

// ============================================
// THUNKS - ПОТОКИ
// ============================================

export function getFlows(shopId, courseId) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const data = await api.fetchFlows(shopId, courseId);
            dispatch(slice.actions.setFlowsSuccess(data));
            return data;
        } catch (error) {
            console.error('Error fetching flows:', error);
            dispatch(slice.actions.hasError(error.message));
            throw error;
        }
    };
}

export function getFlow(shopId, courseId, flowId) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const data = await api.fetchFlow(shopId, courseId, flowId);
            dispatch(slice.actions.setCurrentFlowSuccess(data));
            return data;
        } catch (error) {
            console.error('Error fetching flow:', error);
            dispatch(slice.actions.hasError(error.message));
            throw error;
        }
    };
}

export function createNewFlow(shopId, courseId, flowData) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const data = await api.createFlow(shopId, courseId, flowData);
            dispatch(slice.actions.addFlowSuccess(data));
            return data;
        } catch (error) {
            console.error('Error creating flow:', error);
            dispatch(slice.actions.hasError(error.message));
            throw error;
        }
    };
}

export function updateExistingFlow(shopId, courseId, flowId, flowData) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const data = await api.updateFlow(shopId, courseId, flowId, flowData);
            dispatch(slice.actions.updateFlowSuccess(data));
            return data;
        } catch (error) {
            console.error('Error updating flow:', error);
            dispatch(slice.actions.hasError(error.message));
            throw error;
        }
    };
}

export function deleteFlow(shopId, courseId, flowId) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            await api.deleteFlow(shopId, courseId, flowId);
            dispatch(slice.actions.deleteFlowSuccess(flowId));
        } catch (error) {
            console.error('Error deleting flow:', error);
            dispatch(slice.actions.hasError(error.message));
            throw error;
        }
    };
}

// ============================================
// THUNKS - ТАРИФЫ
// ============================================

export function getTariffs(shopId, courseId, flowId) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const data = await api.fetchTariffs(shopId, courseId, flowId);
            dispatch(slice.actions.setTariffsSuccess(data));
            return data;
        } catch (error) {
            console.error('Error fetching tariffs:', error);
            dispatch(slice.actions.hasError(error.message));
            throw error;
        }
    };
}

export function getTariff(shopId, courseId, flowId, tariffId) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const data = await api.fetchTariff(shopId, courseId, flowId, tariffId);
            dispatch(slice.actions.setCurrentTariffSuccess(data));
            return data;
        } catch (error) {
            console.error('Error fetching tariff:', error);
            dispatch(slice.actions.hasError(error.message));
            throw error;
        }
    };
}

export function createTariff(shopId, courseId, flowId, tariffData) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const data = await api.createTariff(shopId, courseId, flowId, tariffData);
            // Debug: log created tariff
            // eslint-disable-next-line no-console
            console.debug('[course-v1] createTariff: created', data);
            // Explicit checklist for manual verification in DevTools
            // eslint-disable-next-line no-console
            console.info('[course-v1][checklist] ✔ Tariff created — verify the following:');
            // eslint-disable-next-line no-console
            console.info(`[course-v1][checklist] 1) POST response contains created tariff id: ${data && data.id ? data.id : '<no-id>'}`);
            // eslint-disable-next-line no-console
            console.info('[course-v1][checklist] 2) POST response contains relation to flow (field like flow_id or flow)');
            // eslint-disable-next-line no-console
            console.info(`[course-v1][checklist] 3) After creation, GET /courses/${shopId}/${courseId}/flows/${flowId}/tariffs should include the created id above`);
            // eslint-disable-next-line no-console
            console.info('[course-v1][checklist] 4) Check Redux state: state.course.tariffs length and ids (FlowTariffsManager logs these after load)');
            // eslint-disable-next-line no-console
            console.info('[course-v1][checklist] 5) If UI does not show the card, check CourseTariffsForm -> CourseSingleTariffForm mapping and keys');
            dispatch(slice.actions.addTariffSuccess(data));
            // After creating a tariff, refresh the tariffs list for this flow
            try {
                // eslint-disable-next-line no-console
                console.info('[course-v1] createTariff: refreshing tariffs list for flow', flowId);
                await dispatch(getTariffs(shopId, courseId, flowId));
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[course-v1] createTariff: failed to refresh tariffs', err);
            }
            return data;
        } catch (error) {
            console.error('Error creating tariff:', error);
            dispatch(slice.actions.hasError(error.message));
            throw error;
        }
    };
}

export function updateTariff(shopId, courseId, flowId, tariffId, tariffData) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const data = await api.updateTariff(shopId, courseId, flowId, tariffId, tariffData);
            dispatch(slice.actions.updateTariffSuccess(data));
            return data;
        } catch (error) {
            console.error('Error updating tariff:', error);
            dispatch(slice.actions.hasError(error.message));
            throw error;
        }
    };
}

export function deleteTariff(shopId, courseId, flowId, tariffId) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            await api.deleteTariff(shopId, courseId, flowId, tariffId);
            dispatch(slice.actions.deleteTariffSuccess(tariffId));
        } catch (error) {
            console.error('Error deleting tariff:', error);
            dispatch(slice.actions.hasError(error.message));
            throw error;
        }
    };
}
